/**
 * Freeze PDP sticky columns when Minimog adds html.prevent-scroll.
 * - Cache visual rects on scroll
 * - Portal nodes to document.body (avoids overflow clipping in main)
 * - Lock body scroll with position:fixed + top:-scrollY
 */
(() => {
  const SELECTORS = [
    ".m-main-product--info .m-product-info--wrapper",
    ".m-main-product--media .m-product-media--wrapper",
  ];
  const html = document.documentElement;
  const body = document.body;
  const rectCache = new Map();
  const pinned = [];
  let lockedScrollY = 0;
  let scrollLocked = false;

  const targets = () => {
    const nodes = [];
    SELECTORS.forEach((selector) => {
      document.querySelectorAll(selector).forEach((el) => nodes.push(el));
    });
    return nodes;
  };

  const updateCache = () => {
    if (pinned.length) return;
    targets().forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) return;
      rectCache.set(el, {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      });
    });
  };

  const lockBodyScroll = (scrollY = window.scrollY || window.pageYOffset || 0) => {
    if (scrollLocked) return;
    lockedScrollY = scrollY;
    body.style.position = "fixed";
    body.style.top = `-${lockedScrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    scrollLocked = true;
  };

  const unlockBodyScroll = () => {
    if (!scrollLocked) return;
    body.style.position = "";
    body.style.top = "";
    body.style.left = "";
    body.style.right = "";
    body.style.width = "";
    scrollLocked = false;
    window.scrollTo(0, lockedScrollY);
  };

  const pin = () => {
    if (pinned.length) return;

    // Capture scroll before DOM moves (spacers/portal can clamp scrollY).
    const scrollY = window.scrollY || window.pageYOffset || 0;
    updateCache();

    targets().forEach((el) => {
      if (el.dataset.marshFrozen === "1") return;

      const live = el.getBoundingClientRect();
      const cached = rectCache.get(el);
      const rect =
        cached && cached.width > 0
          ? cached
          : { top: live.top, left: live.left, width: live.width, height: live.height };

      if (!rect.width && !rect.height) return;

      const parent = el.parentNode;
      if (!parent) return;

      const spacer = document.createElement("div");
      spacer.className = "marsh-modal-freeze-spacer";
      spacer.setAttribute("aria-hidden", "true");
      spacer.style.width = `${rect.width}px`;
      spacer.style.height = `${rect.height}px`;
      spacer.style.flexShrink = "0";
      spacer.style.pointerEvents = "none";
      spacer.style.visibility = "hidden";

      parent.insertBefore(spacer, el);

      pinned.push({
        el,
        spacer,
        parent,
        position: el.style.position,
        top: el.style.top,
        left: el.style.left,
        width: el.style.width,
        height: el.style.height,
        right: el.style.right,
        zIndex: el.style.zIndex,
        margin: el.style.margin,
      });

      el.dataset.marshFrozen = "1";
      body.appendChild(el);
      el.style.setProperty("position", "fixed", "important");
      el.style.setProperty("top", `${rect.top}px`, "important");
      el.style.setProperty("left", `${rect.left}px`, "important");
      el.style.setProperty("width", `${rect.width}px`, "important");
      el.style.setProperty("right", "auto", "important");
      el.style.setProperty("margin", "0", "important");
      el.style.setProperty("z-index", "2", "important");
    });

    lockBodyScroll(scrollY);
  };

  const unpin = () => {
    while (pinned.length) {
      const state = pinned.pop();
      const { el, spacer, parent } = state;

      el.style.position = state.position;
      el.style.top = state.top;
      el.style.left = state.left;
      el.style.width = state.width;
      el.style.height = state.height;
      el.style.right = state.right;
      el.style.zIndex = state.zIndex;
      el.style.margin = state.margin;
      el.style.removeProperty("z-index");
      delete el.dataset.marshFrozen;

      if (parent) {
        if (spacer && spacer.parentNode === parent) {
          parent.insertBefore(el, spacer);
          parent.removeChild(spacer);
        } else {
          parent.appendChild(el);
          if (spacer && spacer.parentNode) spacer.parentNode.removeChild(spacer);
        }
      } else if (spacer && spacer.parentNode) {
        spacer.parentNode.removeChild(spacer);
      }
    }

    unlockBodyScroll();
    updateCache();
  };

  const originalAdd = DOMTokenList.prototype.add;
  const originalRemove = DOMTokenList.prototype.remove;

  DOMTokenList.prototype.add = function (...tokens) {
    if (this === html.classList && tokens.includes("prevent-scroll")) {
      pin();
    }
    return originalAdd.apply(this, tokens);
  };

  DOMTokenList.prototype.remove = function (...tokens) {
    const result = originalRemove.apply(this, tokens);
    if (
      this === html.classList &&
      tokens.includes("prevent-scroll") &&
      !html.classList.contains("prevent-scroll")
    ) {
      unpin();
    }
    return result;
  };

  window.addEventListener("scroll", updateCache, { passive: true });
  window.addEventListener("resize", updateCache, { passive: true });
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", updateCache, { once: true });
  } else {
    updateCache();
  }
})();
